import { ViperSelect } from "@/components";
import { convertLimitedListUsersToIds } from "@/containers/Project/components/ProjectTeam/helpers/convertLimitedListUsersToIds";
import { defaultUsersForProject } from "@/containers/Project/components/ProjectTeam/helpers/defaultUsersForProject";
import { CONTENT_WIDTH, FieldNames, FormType } from "@/enums";
import { IProjectTeamRole, ISelectOption, ITranslation } from "@/interfaces";
import { IRegionalProjectRole } from "@/interfaces/Project/Roles/IRegionalProjectRole";
import {
  useFormPropertyValueLookupProvider,
  useProjectContext,
} from "@/providers";
import {
  useGetProjectRoleQuery,
  useSuggestProjectRolesMutation,
} from "@/services";
import { Col, Form, Modal, Row, Select } from "antd";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { v4 } from "uuid";

const { Option } = Select;

interface IProps {
  isOpen: boolean;
  regionId: string;
  currentProjectTeamRolesByFunctionalArea: {
    [functionalArea: string]: IProjectTeamRole[];
  };
  functionalAreas: { [functionalArea: string]: ITranslation };
  onClose(): void;
  onOk(role: IProjectTeamRole): void;
}

export const AddProjectTeamRoleModal = ({
  isOpen,
  regionId,
  currentProjectTeamRolesByFunctionalArea,
  functionalAreas,
  onClose,
  onOk,
}: IProps) => {
  const intl = useIntl();
  const { getPropertyValueDisplay } = useFormPropertyValueLookupProvider();
  const [form] = Form.useForm();
  const { project } = useProjectContext();

  const [projectRoleSuggestions, setProjectRoleSuggestions] = useState<
    ISelectOption[]
  >([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState<string>();

  const [selectedFunctionalArea, setSelectedFunctionalArea] =
    useState<string>();

  const { data: projectRoleToAdd } = useGetProjectRoleQuery(
    selectedSuggestion,
    {
      skip: !selectedSuggestion,
    }
  );

  const [
    suggestProjectRoles,
    { data: suggestedProjectRoles, isLoading: fetchingProjectRolesSuggestions },
  ] = useSuggestProjectRolesMutation();

  useEffect(() => {
    if (!suggestedProjectRoles) return;

    setProjectRoleSuggestions(
      suggestedProjectRoles.map(
        ({
          document: { id, name },
        }: {
          document: { id: string; name: string };
        }) => ({
          value: id,
          label: name,
        })
      )
    );
  }, [suggestedProjectRoles]);

  const suggestRoles = (value: string): void => {
    suggestProjectRoles({
      query: value,
      includeEmptyProjectType: true,
    });
  };

  const clearSuggestions = (): void => {
    setProjectRoleSuggestions([]);
  };

  const onModalOk = (): void => {
    if (projectRoleToAdd) {
      const formValue = form.getFieldsValue(true);

      const regionalRole = projectRoleToAdd.regionalProjectRoles?.find(
        (regionalProjectRole: IRegionalProjectRole) =>
          !regionalProjectRole.isDeleted &&
          regionalProjectRole.region === regionId &&
          regionalProjectRole.functionalArea ===
            formValue.functionalAreaForNewRole
      );

      const newRole: IProjectTeamRole = {
        id: v4(),
        name: projectRoleToAdd.name,
        roleId: projectRoleToAdd.id,
        functionalAreaId: formValue.functionalAreaForNewRole,
        regionId: regionId,
        limitedListUsers: regionalRole
          ? convertLimitedListUsersToIds(regionalRole.limitedListUsers)
          : [],
        assignedUsers: regionalRole
          ? defaultUsersForProject(regionalRole, project)
          : [],
        isCoreTeam: false,
        isMilestoneEditor: projectRoleToAdd.isMilestoneEditor,
        isDeleted: false,
      };

      onOk(newRole);
      clearSuggestions();
      setSelectedSuggestion(undefined);
      form.resetFields();
      onClose();
    }
  };

  const setProjectRoleSuggestionAndClear = (selected: string): void => {
    setSelectedSuggestion(selected);
    form.setFieldsValue({ functionalAreaForNewRole: null });
    setSelectedFunctionalArea(undefined);
  };

  return (
    <Modal
      title={intl.formatMessage(
        { id: "projects.teamAddRoleModalTitle" },
        {
          region: getPropertyValueDisplay(
            FormType.Intake,
            FieldNames.Region,
            regionId
          ),
        }
      )}
      open={isOpen}
      onCancel={onClose}
      onOk={onModalOk}
      okButtonProps={{
        disabled:
          !projectRoleToAdd || !selectedSuggestion || !selectedFunctionalArea,
      }}
    >
      {functionalAreas && (
        <>
          <Form form={form} layout="vertical">
            <Row>
              <Col xs={CONTENT_WIDTH.max}>
                <Form.Item
                  name="addRoleId"
                  label={intl.formatMessage({ id: "projects.addRoleLabel" })}
                >
                  <ViperSelect
                    onChange={setProjectRoleSuggestionAndClear}
                    suggestions={projectRoleSuggestions}
                    loading={fetchingProjectRolesSuggestions}
                    value={null}
                    onFetchSuggestions={suggestRoles}
                    placeholder={intl.formatMessage({
                      id: "editProjectTemplate.milestoneModal.searchRoles",
                    })}
                    data-test-id="add-role-select"
                    onBlur={clearSuggestions}
                  />
                </Form.Item>
                <Form.Item
                  name="functionalAreaForNewRole"
                  label={intl.formatMessage({
                    id: "projects.addRoleFunctionalAreaLabel",
                  })}
                >
                  <Select
                    data-test-id="project-team-region-select"
                    placeholder={intl.formatMessage({
                      id: "editProjectTemplate.milestoneModal.functionalArea",
                    })}
                    disabled={!selectedSuggestion}
                    onChange={setSelectedFunctionalArea}
                  >
                    {Object.keys(functionalAreas)
                      .filter(
                        (functionalAreaId) =>
                          !currentProjectTeamRolesByFunctionalArea[
                            functionalAreaId
                          ]?.some(
                            (role: IProjectTeamRole) =>
                              role.roleId === selectedSuggestion
                          )
                      )
                      .map((functionalArea) => (
                        <Option key={functionalArea} value={functionalArea}>
                          {getPropertyValueDisplay(
                            FormType.Intake,
                            FieldNames.FunctionalArea,
                            functionalArea
                          )}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </>
      )}
    </Modal>
  );
};
